import React, { useState } from "react"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap"
import Switch from "components/Shared/Switch"
import ImageUrlField from "components/Shared/ImageUrlField"
import {
  BUILDER_WHATSAPP_FIELD,
  FEATURE_TOGGLES,
  WEBSITE_ANIMATION_OPTIONS,
  HERO_LAYOUT_OPTIONS,
  WEBSITE_SECTION_OPTIONS,
  BUILDER_AVAILABLE_LAYOUT_IDS,
  cloneWebsiteConfig,
  createEmptyLocalized,
  getDeepValue,
  getTemplateSwitchSummary,
  normalizeHomeSectionOrder,
  normalizeWebsiteUrl,
  setDeepValue,
  setLocalizedDeep,
  syncPromoMessages,
  syncPrimaryThemeColor,
  applyWebThemeToForm,
  PAGE_BACKGROUND_DIRECTIONS,
  resolvePageBackgroundCss,
} from "modules/website-builder/constants"
import { filterWebsiteBuilderFeatureToggles } from "modules/website-builder/constants/planFeatures"

const emptyFooterLink = () => ({ label: createEmptyLocalized(), href: "" })
const emptyPlatform = () => ({ id: "", label: "", url: "", iconUrl: "" })
const emptyPayment = () => ({ id: "", label: "", iconUrl: "" })

function ImageField({ form, path, label, t, onChange, purpose, md = 6, compact = false }) {
  return (
    <ImageUrlField
      label={label}
      value={getDeepValue(form, path) || ""}
      onChange={url => onChange(prev => setDeepValue(prev, path, url))}
      t={t}
      md={md}
      purpose={purpose}
      compact={compact}
    />
  )
}

export function LocalizedField({ form, path, label, t, onChange }) {
  const value = getDeepValue(form, path) || createEmptyLocalized()
  return (
    <Row className="mb-3">
      <Col md="12">
        <Label>{t(label)}</Label>
      </Col>
      <Col md="6">
        <Input
          value={value.en || ""}
          onChange={e => onChange(prev => setLocalizedDeep(prev, path, "en", e.target.value))}
          placeholder={t("English")}
        />
      </Col>
      <Col md="6">
        <Input
          value={value.ar || ""}
          onChange={e => onChange(prev => setLocalizedDeep(prev, path, "ar", e.target.value))}
          placeholder={t("Arabic")}
        />
      </Col>
    </Row>
  )
}

export function ColorField({ form, path, label, t, onChange, fallback = "#000000" }) {
  return (
    <Col md="4" lg="3">
      <div className="mb-3">
        <Label>{t(label)}</Label>
        <Input
          type="color"
          value={getDeepValue(form, path) || fallback}
          onChange={e => onChange(prev => setDeepValue(prev, path, e.target.value))}
        />
      </div>
    </Col>
  )
}

export function TextField({ form, path, label, t, onChange, type = "text", md = 6 }) {
  return (
    <Col md={md}>
      <div className="mb-3">
        <Label>{t(label)}</Label>
        <Input
          type={type}
          value={getDeepValue(form, path) ?? ""}
          onChange={e =>
            onChange(prev =>
              setDeepValue(
                prev,
                path,
                type === "number" ? Number(e.target.value) : e.target.value
              )
            )
          }
        />
      </div>
    </Col>
  )
}

export function WebThemesTab({ form, webThemes, publishedWebThemeSlug, t, onChange, embedded = false }) {
  if (!webThemes?.length) {
    return (
      <p className="text-muted mb-0">
        {t("No theme presets are available yet. Contact your platform administrator.")}
      </p>
    )
  }

  return (
    <>
      {!embedded ? (
        <Row className="mb-3">
          <Col md="12">
            <p className="text-muted mb-0">
              {t(
                "Pick a color theme for your storefront. You can still fine-tune colors in Brand & Theme after applying a preset."
              )}
            </p>
          </Col>
        </Row>
      ) : null}

      <Row>
        {webThemes.map(theme => {
          const isSelected = form.webThemeSlug === theme.slug
          const isLive = publishedWebThemeSlug === theme.slug
          const preview = theme.preview || {}
          const bgStart = preview.pageBackgroundStart || preview.primaryLight || preview.primaryColor
          const bgEnd = preview.pageBackgroundEnd || bgStart
          const cardPreviewBg = resolvePageBackgroundCss(
            { colorStart: bgStart, colorEnd: bgEnd, direction: "to bottom" },
            bgStart
          )

          return (
            <Col md="6" lg="4" key={theme.slug} className="mb-3">
              <Card
                className={`h-100 border ${isSelected ? "border-primary border-2 shadow-sm" : ""}`}
                style={{
                  cursor: "pointer",
                  background: isSelected ? "rgba(85, 110, 230, 0.04)" : undefined,
                }}
                onClick={() => onChange(prev => applyWebThemeToForm(prev, theme))}
              >
                <CardBody>
                  <div
                    className="rounded mb-3 overflow-hidden border"
                    style={{ display: "flex", height: 48 }}
                    aria-hidden
                  >
                    <div style={{ flex: 1, background: cardPreviewBg }} />
                    {[preview.primaryColor, preview.secondaryColor]
                      .filter(Boolean)
                      .map(color => (
                        <div key={`${theme.slug}-${color}`} style={{ flex: 1, background: color }} />
                      ))}
                  </div>

                  <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                    <h5 className="mb-0">{theme.name}</h5>
                    <div className="d-flex flex-wrap gap-1 justify-content-end">
                      {isSelected ? <Badge color="primary">{t("Selected")}</Badge> : null}
                      {isLive ? (
                        <Badge color="success" pill>
                          {t("Live")}
                        </Badge>
                      ) : null}
                    </div>
                  </div>

                  <p className="text-muted small mb-0">{theme.description}</p>
                </CardBody>
              </Card>
            </Col>
          )
        })}
      </Row>
    </>
  )
}

export function TemplateTab({
  form,
  templates,
  publishedTemplateId,
  t,
  onChange,
  embedded = false,
}) {
  const selectedTemplate = templates.find(item => item.id === form.templateId)
  const switchSummary = getTemplateSwitchSummary(
    publishedTemplateId,
    form.templateId,
    templates,
    WEBSITE_SECTION_OPTIONS
  )

  const isTemplateSelectable = template =>
    template?.available !== false &&
    (!BUILDER_AVAILABLE_LAYOUT_IDS.size || BUILDER_AVAILABLE_LAYOUT_IDS.has(template.id))

  if (!templates?.length) {
    return (
      <p className="text-muted mb-0">
        {t("No layout templates are available yet.")}
      </p>
    )
  }

  return (
    <>
      {!embedded ? (
        <Row className="mb-3">
          <Col md="12">
            <p className="text-muted mb-0">
              {t(
                "Choose a layout preset. Brand colors, copy, and section visibility can still be customized in the other tabs."
              )}
            </p>
          </Col>
        </Row>
      ) : null}

      <Row>
        {templates.map(template => {
          const isSelected = form.templateId === template.id
          const isLive = publishedTemplateId === template.id
          const selectable = isTemplateSelectable(template)

          return (
            <Col md="6" key={template.id} className="mb-3">
              <Card
                className={`h-100 border ${isSelected ? "border-primary border-2 shadow-sm" : ""} ${
                  selectable ? "" : "opacity-75"
                }`}
                style={{
                  cursor: selectable ? "pointer" : "not-allowed",
                  background: isSelected ? "rgba(85, 110, 230, 0.04)" : undefined,
                }}
                onClick={() => {
                  if (!selectable) return
                  onChange(prev => ({
                    ...prev,
                    templateId: template.id,
                    template: {
                      ...(prev.template || {}),
                      sectionOrder: template.defaultSectionOrder || prev.template?.sectionOrder,
                    },
                  }))
                }}
              >
                <CardBody>
                  <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                    <h5 className="mb-0">{template.name}</h5>
                    <div className="d-flex flex-wrap gap-1 justify-content-end">
                      {isSelected ? (
                        <Badge color="primary">{t("Selected")}</Badge>
                      ) : null}
                      {isLive ? (
                        <Badge color="success" pill>
                          {t("Live")}
                        </Badge>
                      ) : null}
                      {!selectable ? (
                        <Badge color="warning" pill>
                          {t("Coming soon")}
                        </Badge>
                      ) : null}
                    </div>
                  </div>
                  <p className="text-muted">{template.description}</p>
                  {template.heroStyle ? (
                    <p className="small mb-1">
                      <strong>{t("Hero")}:</strong> {template.heroStyle}
                    </p>
                  ) : null}
                  {template.categoryStyle ? (
                    <p className="small mb-2">
                      <strong>{t("Categories")}:</strong> {template.categoryStyle}
                    </p>
                  ) : null}
                  {Array.isArray(template.highlights) && template.highlights.length ? (
                    <ul className="small mb-0 ps-3">
                      {template.highlights.map(item => (
                        <li key={`${template.id}-${item}`}>{item}</li>
                      ))}
                    </ul>
                  ) : null}
                </CardBody>
              </Card>
            </Col>
          )
        })}
      </Row>

      {selectedTemplate ? (
        <Card className="border bg-light">
          <CardBody>
            <h5 className="mb-2">{t("What this template includes")}</h5>
            <p className="text-muted mb-2">
              {selectedTemplate.description}
            </p>
            <div className="d-flex flex-wrap gap-2 mb-2">
              {(selectedTemplate.defaultSectionOrder || []).map(sectionId => {
                const meta = WEBSITE_SECTION_OPTIONS.find(item => item.id === sectionId)
                return (
                  <Badge key={sectionId} color="light" className="text-dark border">
                    {meta?.label || sectionId}
                  </Badge>
                )
              })}
            </div>
            {switchSummary ? (
              <div className="mt-3 pt-3 border-top">
                <h6 className="mb-2">
                  {t("Changes vs live published template ({{name}})", {
                    name: switchSummary.fromName,
                  })}
                </h6>
                {switchSummary.heroStyle ? (
                  <p className="small mb-1">
                    <strong>{t("Hero layout")}:</strong> {switchSummary.heroStyle}
                  </p>
                ) : null}
                {switchSummary.categoryStyle ? (
                  <p className="small mb-2">
                    <strong>{t("Category layout")}:</strong> {switchSummary.categoryStyle}
                  </p>
                ) : null}
                {switchSummary.addedSections.length ? (
                  <p className="small mb-1">
                    <strong>{t("Sections added in default order")}:</strong>{" "}
                    {switchSummary.addedSections.join(", ")}
                  </p>
                ) : null}
                {switchSummary.removedSections.length ? (
                  <p className="small mb-0">
                    <strong>{t("Sections removed from default order")}:</strong>{" "}
                    {switchSummary.removedSections.join(", ")}
                  </p>
                ) : null}
                <p className="text-muted small mt-2 mb-0">
                  {t("Publish to apply template and section order changes on the live website.")}
                </p>
              </div>
            ) : null}
          </CardBody>
        </Card>
      ) : null}
    </>
  )
}

export function TemplatesTab({
  form,
  webThemes,
  templates,
  publishedWebThemeSlug,
  publishedTemplateId,
  t,
  onChange,
}) {
  const [activeSection, setActiveSection] = useState("color-themes")

  return (
    <>
      <Nav tabs className="nav-tabs-custom mb-3">
        <NavItem>
          <NavLink
            className={activeSection === "color-themes" ? "active" : ""}
            onClick={() => setActiveSection("color-themes")}
            style={{ cursor: "pointer" }}
          >
            {t("Color themes")}
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={activeSection === "layout-templates" ? "active" : ""}
            onClick={() => setActiveSection("layout-templates")}
            style={{ cursor: "pointer" }}
          >
            {t("Layout templates")}
          </NavLink>
        </NavItem>
      </Nav>

      <TabContent activeTab={activeSection}>
        <TabPane tabId="color-themes">
          <p className="text-muted small mb-3">
            {t(
              "Apply a coordinated color palette and page background. Adjust individual tokens anytime under Brand & Theme."
            )}
          </p>
          <WebThemesTab
            embedded
            form={form}
            webThemes={webThemes}
            publishedWebThemeSlug={publishedWebThemeSlug}
            t={t}
            onChange={onChange}
          />
        </TabPane>

        <TabPane tabId="layout-templates">
          <p className="text-muted small mb-3">
            {t(
              "Choose how the storefront is arranged — header, hero, categories, and home sections. Cart, checkout, and account flows stay the same across layouts."
            )}
          </p>
          <TemplateTab
            embedded
            form={form}
            templates={templates}
            publishedTemplateId={publishedTemplateId}
            t={t}
            onChange={onChange}
          />
        </TabPane>
      </TabContent>
    </>
  )
}

export function BrandThemeTab({
  form,
  t,
  onChange,
  savedThemeBaseline,
  savedThemeBaselineAt,
  savingThemeBaseline,
  restoringThemeBaseline,
  onSaveThemeBaseline,
  onRestoreThemeBaseline,
}) {
  return (
    <Row>
      <Col md="12" className="mb-3">
        <Card className="border bg-light">
          <CardBody>
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
              <div>
                <h5 className="mb-1">{t("My Saved Design")}</h5>
                <p className="text-muted small mb-0">
                  {t(
                    "Save your full website setup — brand, colors, content, home sections, footer, and layout. Stored separately and only updates when you click Save My Design."
                  )}
                </p>
                {savedThemeBaselineAt ? (
                  <p className="text-muted small mb-0 mt-2">
                    {t("Last saved at {{date}}", {
                      date: new Date(savedThemeBaselineAt).toLocaleString(),
                    })}
                  </p>
                ) : (
                  <p className="text-muted small mb-0 mt-2">
                    {t("No saved design yet. Configure your website, then save your snapshot.")}
                  </p>
                )}
                <p className="text-muted small mb-0 mt-2">
                  {t(
                    "If you published before experimenting, you can also use Discard Draft in the header to revert all draft changes."
                  )}
                </p>
              </div>
              <div className="d-flex flex-wrap gap-2">
                <Button
                  color="primary"
                  outline
                  onClick={onRestoreThemeBaseline}
                  disabled={!savedThemeBaseline || restoringThemeBaseline}
                >
                  {restoringThemeBaseline ? t("Restoring...") : t("Reset to Saved Design")}
                </Button>
                <Button color="primary" onClick={onSaveThemeBaseline} disabled={savingThemeBaseline}>
                  {savingThemeBaseline ? t("Saving...") : t("Save My Design")}
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      </Col>

      <TextField form={form} path="brand.name" label="Brand Name" t={t} onChange={onChange} />
      <Col md="12">
        <p className="text-muted small mb-3">
          {t(
            "Shown in the site header (when no logo), browser tab title fallback, and logo alt text. The storefront footer shows the logo image only, not the name text."
          )}
        </p>
      </Col>
      <ImageField form={form} path="brand.logoUrl" label="Logo" t={t} onChange={onChange} purpose="logo" />
      <ImageField form={form} path="brand.faviconUrl" label="Favicon" t={t} onChange={onChange} purpose="favicon" />
      <Col md="4" lg="3">
        <div className="mb-3">
          <Label>{t("Primary Color")}</Label>
          <Input
            type="color"
            value={getDeepValue(form, "theme.primaryColor") || "#5828A3"}
            onChange={e => onChange(prev => syncPrimaryThemeColor(prev, e.target.value))}
          />
          <small className="text-muted d-block mt-1">
            {t("Also updates primary buttons and radio accents")}
          </small>
        </div>
      </Col>
      <ColorField form={form} path="theme.secondaryColor" label="Secondary Color" t={t} onChange={onChange} />
      <ColorField form={form} path="theme.primaryLight" label="Primary Light" t={t} onChange={onChange} />
      <ColorField form={form} path="theme.primaryDeep" label="Primary Deep" t={t} onChange={onChange} />
      <TextField form={form} path="theme.fontFamily" label="Font Family" t={t} onChange={onChange} md={6} />
      <TextField form={form} path="theme.fontDisplay" label="Display Font" t={t} onChange={onChange} md={6} />

      <Col md="12">
        <h5 className="mt-2">{t("Page Background")}</h5>
        <p className="text-muted small">
          {t(
            "Set two colors for a gradient, or use the same color on both for a solid background. Applies to the storefront page canvas."
          )}
        </p>
      </Col>
      <ColorField
        form={form}
        path="theme.pageBackground.colorStart"
        label="Background start"
        t={t}
        onChange={onChange}
        fallback="#F3E8FF"
      />
      <ColorField
        form={form}
        path="theme.pageBackground.colorEnd"
        label="Background end"
        t={t}
        onChange={onChange}
        fallback="#F3E8FF"
      />
      <Col md="4" lg="3">
        <div className="mb-3">
          <Label>{t("Gradient direction")}</Label>
          <Input
            type="select"
            value={getDeepValue(form, "theme.pageBackground.direction") || "to bottom"}
            onChange={e =>
              onChange(prev => setDeepValue(prev, "theme.pageBackground.direction", e.target.value))
            }
          >
            {PAGE_BACKGROUND_DIRECTIONS.map(option => (
              <option key={option.id} value={option.id}>
                {t(option.label)}
              </option>
            ))}
          </Input>
        </div>
      </Col>
      <Col md="4" lg="3">
        <div className="mb-3">
          <Label>{t("Preview")}</Label>
          <div
            className="rounded border"
            style={{
              height: 48,
              background: resolvePageBackgroundCss(
                getDeepValue(form, "theme.pageBackground"),
                getDeepValue(form, "theme.primaryLight") || "#F7FAFC"
              ),
            }}
            aria-hidden
          />
        </div>
      </Col>

      <Col md="12"><h5 className="mt-2">{t("Buttons")}</h5></Col>
      <ColorField form={form} path="theme.buttons.backgroundColor" label="Button Background" t={t} onChange={onChange} />
      <ColorField form={form} path="theme.buttons.textColor" label="Button Text" t={t} onChange={onChange} />
      <TextField form={form} path="theme.buttons.borderRadius" label="Button Radius" t={t} onChange={onChange} type="number" md={3} />
      <TextField form={form} path="theme.buttons.compactBorderRadius" label="Compact Button Radius" t={t} onChange={onChange} type="number" md={3} />

      <Col md="12"><h5 className="mt-2">{t("Inputs")}</h5></Col>
      <ColorField form={form} path="theme.inputs.backgroundColor" label="Input Background" t={t} onChange={onChange} />
      <ColorField form={form} path="theme.inputs.textColor" label="Input Text" t={t} onChange={onChange} />
      <ColorField form={form} path="theme.inputs.borderColor" label="Input Border" t={t} onChange={onChange} />
      <TextField form={form} path="theme.inputs.borderRadius" label="Input Radius" t={t} onChange={onChange} type="number" md={3} />

      <Col md="12"><h5 className="mt-2">{t("Radio Controls")}</h5></Col>
      <TextField form={form} path="theme.radios.borderRadius" label="Radio Radius" t={t} onChange={onChange} type="number" md={3} />
      <ColorField form={form} path="theme.radios.borderColor" label="Radio Border" t={t} onChange={onChange} />
      <ColorField form={form} path="theme.radios.selectedBorderColor" label="Radio Selected Border" t={t} onChange={onChange} />
      <ColorField form={form} path="theme.radios.textColor" label="Radio Text" t={t} onChange={onChange} />
      <ColorField form={form} path="theme.radios.selectedTextColor" label="Radio Selected Text" t={t} onChange={onChange} />
      <ColorField form={form} path="theme.radios.dotColor" label="Radio Dot" t={t} onChange={onChange} />
      <ColorField form={form} path="theme.radios.backgroundColor" label="Radio Background" t={t} onChange={onChange} />
    </Row>
  )
}

const emptyTrustStripItem = () => ({
  title: createEmptyLocalized(),
  copy: createEmptyLocalized(),
})

function TrustStripEditor({ form, t, onChange }) {
  const trustStrip = form.content?.trustStrip || { label: createEmptyLocalized(), items: [] }
  const items = trustStrip.items || []

  const updateItems = nextItems =>
    onChange(prev =>
      setDeepValue(prev, "content.trustStrip", {
        ...(getDeepValue(prev, "content.trustStrip") || trustStrip),
        items: nextItems,
      })
    )

  return (
    <>
      <Col md="12"><h5 className="mt-2">{t("Trust Strip")}</h5></Col>
      <Col md="12">
        <p className="text-muted small mb-2">
          {t(
            "Three trust badges shown below the hero carousel on the home page."
          )}
        </p>
      </Col>
      <LocalizedField
        form={form}
        path="content.trustStrip.label"
        label="Trust Strip Label"
        t={t}
        onChange={onChange}
      />
      <Col md="12">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <Label className="mb-0">{t("Trust Strip Items")}</Label>
          <Button
            size="sm"
            color="primary"
            outline
            onClick={() => updateItems([...items, emptyTrustStripItem()])}
          >
            {t("Add Item")}
          </Button>
        </div>
        {items.length === 0 ? (
          <p className="text-muted small">
            {t("Add trust strip items with a title and short description.")}
          </p>
        ) : null}
        {items.map((item, index) => (
          <Card key={`trust-${index}`} className="border mb-2">
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Label className="text-muted small mb-0">
                  {t("Item {{number}}", { number: index + 1 })}
                </Label>
                <Button
                  size="sm"
                  color="danger"
                  outline
                  onClick={() => updateItems(items.filter((_, i) => i !== index))}
                >
                  {t("Remove")}
                </Button>
              </div>
              <Row>
                <Col md="6">
                  <Label className="small">{t("Title (English)")}</Label>
                  <Input
                    value={item.title?.en || ""}
                    placeholder={t("English")}
                    onChange={e => {
                      const next = cloneWebsiteConfig(items)
                      next[index] = {
                        ...next[index],
                        title: { ...next[index].title, en: e.target.value },
                      }
                      updateItems(next)
                    }}
                  />
                </Col>
                <Col md="6">
                  <Label className="small">{t("Title (Arabic)")}</Label>
                  <Input
                    value={item.title?.ar || ""}
                    placeholder={t("Arabic")}
                    onChange={e => {
                      const next = cloneWebsiteConfig(items)
                      next[index] = {
                        ...next[index],
                        title: { ...next[index].title, ar: e.target.value },
                      }
                      updateItems(next)
                    }}
                  />
                </Col>
                <Col md="6" className="mt-2">
                  <Label className="small">{t("Description (English)")}</Label>
                  <Input
                    value={item.copy?.en || ""}
                    placeholder={t("English")}
                    onChange={e => {
                      const next = cloneWebsiteConfig(items)
                      next[index] = {
                        ...next[index],
                        copy: { ...next[index].copy, en: e.target.value },
                      }
                      updateItems(next)
                    }}
                  />
                </Col>
                <Col md="6" className="mt-2">
                  <Label className="small">{t("Description (Arabic)")}</Label>
                  <Input
                    value={item.copy?.ar || ""}
                    placeholder={t("Arabic")}
                    onChange={e => {
                      const next = cloneWebsiteConfig(items)
                      next[index] = {
                        ...next[index],
                        copy: { ...next[index].copy, ar: e.target.value },
                      }
                      updateItems(next)
                    }}
                  />
                </Col>
              </Row>
            </CardBody>
          </Card>
        ))}
      </Col>
    </>
  )
}

function PromoMessagesEditor({ form, t, onChange }) {
  const messages = form.content?.promoMessages || form.topAd?.messages || []

  const updateMessages = nextMessages => onChange(prev => syncPromoMessages(prev, nextMessages))

  return (
    <Col md="12">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="mb-0">{t("Promo Bar Messages")}</h5>
        <Button
          size="sm"
          color="primary"
          outline
          onClick={() => updateMessages([...messages, createEmptyLocalized()])}
        >
          {t("Add Message")}
        </Button>
      </div>
      {messages.length === 0 ? (
        <p className="text-muted small">
          {t("Add promo bar messages. Multiple messages scroll across the top bar.")}
        </p>
      ) : null}
      {messages.map((message, index) => (
        <Card key={`promo-${index}`} className="border mb-2">
          <CardBody>
            <Label className="text-muted small mb-2">
              {t("Message {{number}}", { number: index + 1 })}
            </Label>
            <Row>
              <Col md="5">
                <Input
                  value={message.en || ""}
                  placeholder={t("English")}
                  onChange={e => {
                    const next = cloneWebsiteConfig(messages)
                    next[index] = { ...next[index], en: e.target.value }
                    updateMessages(next)
                  }}
                />
              </Col>
              <Col md="5">
                <Input
                  value={message.ar || ""}
                  placeholder={t("Arabic")}
                  onChange={e => {
                    const next = cloneWebsiteConfig(messages)
                    next[index] = { ...next[index], ar: e.target.value }
                    updateMessages(next)
                  }}
                />
              </Col>
              <Col md="2" className="d-flex align-items-start">
                <Button
                  size="sm"
                  color="danger"
                  outline
                  onClick={() => updateMessages(messages.filter((_, i) => i !== index))}
                >
                  {t("Remove")}
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>
      ))}
    </Col>
  )
}

export function HomeTab({ form, t, onChange }) {
  const orderedSections = normalizeHomeSectionOrder(form?.template?.sectionOrder)
  const heroLayout = form.hero?.layout === "split" ? "split" : "carousel"
  const useMenuBanners = !!form.hero?.useMenuBanners

  const moveSection = (sectionId, direction) => {
    onChange(prev => {
      const order = normalizeHomeSectionOrder(prev.template?.sectionOrder)
      const index = order.indexOf(sectionId)
      if (index < 0) return prev
      const target = direction === "up" ? index - 1 : index + 1
      if (target < 0 || target >= order.length) return prev
      ;[order[index], order[target]] = [order[target], order[index]]
      return setDeepValue(prev, "template.sectionOrder", order)
    })
  }

  return (
    <Row>
      <Col md="12"><h5 className="mt-2">{t("Home Sections")}</h5></Col>
      <Col md="12">
        <p className="text-muted small mb-2">
          {t(
            "Show or hide home page sections and drag order with Up/Down. The storefront uses one standard layout; alternate layout patterns will be added later."
          )}
        </p>
      </Col>
      {orderedSections.map((sectionId, index) => {
        const meta = WEBSITE_SECTION_OPTIONS.find(item => item.id === sectionId)
        const visibilityChecked = form.template?.sectionVisibility?.[sectionId] !== false
        return (
          <Col md="12" key={sectionId}>
            <Card className="border mb-2">
              <CardBody className="d-flex flex-wrap justify-content-between align-items-center gap-2 py-2">
                <div className="d-flex align-items-center gap-2">
                  <strong>{meta?.label || sectionId}</strong>
                  {sectionId === "orderContextBar" ? (
                    <span className="text-muted small">
                      {t(
                        "Pinned blue bar on home. When off, home page hides Delivery/Pickup in header too. Menu pages use Features → Header Delivery/Pickup."
                      )}
                    </span>
                  ) : null}
                  {sectionId === "whatsappFab" &&
                  !form.social?.whatsapp &&
                  !form.contact?.phone ? (
                    <span className="text-muted small">
                      {t("Add a WhatsApp number under Footer & Contact to show this button.")}
                    </span>
                  ) : null}
                  <Switch
                    label={visibilityChecked ? t("Visible") : t("Hidden")}
                    isChecked={visibilityChecked}
                    onChange={checked =>
                      onChange(prev => setDeepValue(prev, `template.sectionVisibility.${sectionId}`, checked))
                    }
                  />
                </div>
                <div className="d-flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    color="light"
                    disabled={index === 0}
                    onClick={() => moveSection(sectionId, "up")}
                  >
                    {t("Up")}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    color="light"
                    disabled={index === orderedSections.length - 1}
                    onClick={() => moveSection(sectionId, "down")}
                  >
                    {t("Down")}
                  </Button>
                </div>
              </CardBody>
            </Card>

            {sectionId === "hero" && visibilityChecked ? (
              <div className="ps-2 ps-md-3 mb-3 border-start border-2">
                <h6 className="mt-2 mb-2">{t("Hero design")}</h6>
                <p className="text-muted small mb-2">
                  {t("Choose how the hero appears when it is visible on the home page.")}
                </p>
                <Row className="gx-3 mb-3">
                  {HERO_LAYOUT_OPTIONS.map(option => {
                    const isSelected = heroLayout === option.id
                    return (
                      <Col md="6" key={option.id} className="mb-2">
                        <Card
                          className={`h-100 border ${isSelected ? "border-primary border-2 shadow-sm" : ""}`}
                          style={{
                            cursor: "pointer",
                            background: isSelected ? "rgba(85, 110, 230, 0.04)" : undefined,
                          }}
                          onClick={() =>
                            onChange(prev => setDeepValue(prev, "hero.layout", option.id))
                          }
                        >
                          <CardBody>
                            <div className="d-flex justify-content-between align-items-start gap-2 mb-1">
                              <strong>{t(option.label)}</strong>
                              {isSelected ? <Badge color="primary">{t("Selected")}</Badge> : null}
                            </div>
                            <p className="text-muted small mb-0">{t(option.description)}</p>
                          </CardBody>
                        </Card>
                      </Col>
                    )
                  })}
                </Row>

                <h6 className="mb-2">{t("Hero content")}</h6>
                <Switch
                  label={t("Use menu banners for hero")}
                  isChecked={useMenuBanners}
                  onChange={checked =>
                    onChange(prev => setDeepValue(prev, "hero.useMenuBanners", checked))
                  }
                />
                <p className="text-muted small mb-3 mt-2">
                  {useMenuBanners
                    ? t(
                        "Hero images come from Menu Banners in the portal. Headline, subheadline, and button text below control the split hero text column and serve as fallbacks on the banner carousel."
                      )
                    : t(
                        "Customize hero copy below. Turn on menu banners above to use portal banner images instead of a single background image."
                      )}
                </p>
                <Row>
                  <LocalizedField form={form} path="hero.headline" label="Hero Headline" t={t} onChange={onChange} />
                  <LocalizedField form={form} path="hero.subheadline" label="Hero Subheadline" t={t} onChange={onChange} />
                  <LocalizedField form={form} path="hero.ctaText" label="Hero CTA Text" t={t} onChange={onChange} />
                  <TextField form={form} path="hero.ctaLink" label="Hero CTA Link" t={t} onChange={onChange} md={6} />
                  {!useMenuBanners ? (
                    <>
                      <LocalizedField
                        form={form}
                        path="hero.badgeFallback"
                        label="Hero Badge Fallback"
                        t={t}
                        onChange={onChange}
                      />
                      <ImageField
                        form={form}
                        path="hero.backgroundImage"
                        label="Hero Background Image"
                        t={t}
                        onChange={onChange}
                        purpose="hero"
                        md={6}
                      />
                    </>
                  ) : null}
                </Row>
              </div>
            ) : null}
          </Col>
        )
      })}

      <Col md="12"><h5 className="mt-2">{t("Top Promo Bar")}</h5></Col>
      <ColorField form={form} path="topAd.backgroundColor" label="Promo Bar Background" t={t} onChange={onChange} />
      <ColorField form={form} path="topAd.textColor" label="Promo Bar Text" t={t} onChange={onChange} />
      <PromoMessagesEditor form={form} t={t} onChange={onChange} />

      <TrustStripEditor form={form} t={t} onChange={onChange} />

      <Col md="12"><h5 className="mt-2">{t("Section Titles")}</h5></Col>
      <Col md="12">
        <p className="text-muted small mb-2">
          {t(
            "Headings shown above home page sections such as Browse Categories, Top Selling, and Offers."
          )}
        </p>
      </Col>
      <LocalizedField form={form} path="content.sections.categories" label="Categories Title" t={t} onChange={onChange} />
      <LocalizedField form={form} path="content.sections.featured" label="Featured Title" t={t} onChange={onChange} />
      <LocalizedField form={form} path="content.sections.offers" label="Offers Title" t={t} onChange={onChange} />
      <LocalizedField form={form} path="content.sectionSubtitles.featured" label="Featured Subtitle" t={t} onChange={onChange} />
      <LocalizedField form={form} path="content.sectionSubtitles.offers" label="Offers Subtitle" t={t} onChange={onChange} />
      <LocalizedField form={form} path="content.offerTitleFallback" label="Offer Title Fallback" t={t} onChange={onChange} />
    </Row>
  )
}

function FooterLinksEditor({ titlePath, linksPath, form, t, onChange }) {
  const links = getDeepValue(form, linksPath) || []

  const updateLinks = nextLinks => onChange(prev => setDeepValue(prev, linksPath, nextLinks))

  return (
    <Col md="12" className="mb-3">
      <LocalizedField form={form} path={titlePath} label={`${titlePath.includes("policies") ? "Policies" : "Important Links"} Title`} t={t} onChange={onChange} />
      <div className="d-flex justify-content-between align-items-center mb-2">
        <Label className="mb-0">{t("Links")}</Label>
        <Button size="sm" color="primary" outline onClick={() => updateLinks([...links, emptyFooterLink()])}>
          {t("Add Link")}
        </Button>
      </div>
      {links.map((link, index) => (
        <Card key={`${linksPath}-${index}`} className="border mb-2">
          <CardBody>
            <Row>
              <Col md="4">
                <Input
                  value={link.label?.en || ""}
                  placeholder={t("English label")}
                  onChange={e => {
                    const next = cloneWebsiteConfig(links)
                    next[index] = { ...next[index], label: { ...next[index].label, en: e.target.value } }
                    updateLinks(next)
                  }}
                />
              </Col>
              <Col md="4">
                <Input
                  value={link.label?.ar || ""}
                  placeholder={t("Arabic label")}
                  onChange={e => {
                    const next = cloneWebsiteConfig(links)
                    next[index] = { ...next[index], label: { ...next[index].label, ar: e.target.value } }
                    updateLinks(next)
                  }}
                />
              </Col>
              <Col md="3">
                <Input
                  value={link.href || ""}
                  placeholder="/path"
                  onChange={e => {
                    const next = cloneWebsiteConfig(links)
                    next[index] = { ...next[index], href: e.target.value }
                    updateLinks(next)
                  }}
                />
              </Col>
              <Col md="1">
                <Button size="sm" color="danger" outline onClick={() => updateLinks(links.filter((_, i) => i !== index))}>
                  ×
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>
      ))}
    </Col>
  )
}

function AppLinkEditor({ platform, form, t, onChange }) {
  const base = `footer.appLinks.${platform}`
  return (
    <Col md="12" className="mb-3">
      <h6>{platform === "ios" ? t("iOS App Link") : t("Android App Link")}</h6>
      <Row>
        <TextField form={form} path={`${base}.url`} label="URL" t={t} onChange={onChange} md={4} />
        <ImageField
          form={form}
          path={`${base}.imageUrl`}
          label="Badge Image"
          t={t}
          onChange={onChange}
          purpose={`app-${platform}`}
          md={4}
        />
        <LocalizedField form={form} path={`${base}.label`} label="Label" t={t} onChange={onChange} />
      </Row>
    </Col>
  )
}

export function FooterContactTab({ form, t, onChange }) {
  const platforms = form.footer?.social?.platforms || []
  const payments = form.footer?.payments || []

  return (
    <Row>
      <Col md="12"><h5>{t("WhatsApp")}</h5></Col>
      <TextField
        form={form}
        path={`social.${BUILDER_WHATSAPP_FIELD.key}`}
        label={BUILDER_WHATSAPP_FIELD.label}
        t={t}
        onChange={onChange}
        md={6}
      />
      <Col md="12">
        <p className="text-muted small mb-3">
          {t(
            "Used by the floating WhatsApp button. Falls back to Contact Phone when empty."
          )}
        </p>
      </Col>

      <Col md="12"><h5 className="mt-2">{t("Footer")}</h5></Col>
      <LocalizedField form={form} path="footer.about" label="Footer About" t={t} onChange={onChange} />
      <ColorField form={form} path="footer.backgroundColor" label="Footer Background" t={t} onChange={onChange} />
      <ColorField form={form} path="footer.textColor" label="Footer Text Color" t={t} onChange={onChange} />
      <LocalizedField form={form} path="footer.copyright" label="Copyright" t={t} onChange={onChange} />

      <AppLinkEditor platform="ios" form={form} t={t} onChange={onChange} />
      <AppLinkEditor platform="android" form={form} t={t} onChange={onChange} />

      <FooterLinksEditor titlePath="footer.policies.title" linksPath="footer.policies.links" form={form} t={t} onChange={onChange} />
      <FooterLinksEditor titlePath="footer.importantLinks.title" linksPath="footer.importantLinks.links" form={form} t={t} onChange={onChange} />

      <Col md="12" className="mb-2">
        <LocalizedField form={form} path="footer.social.title" label="Footer Social Title" t={t} onChange={onChange} />
        <div className="d-flex justify-content-between align-items-center mb-2">
          <Label className="mb-0">{t("Footer Social Platforms")}</Label>
          <Button
            size="sm"
            color="primary"
            outline
            onClick={() =>
              onChange(prev => setDeepValue(prev, "footer.social.platforms", [...platforms, emptyPlatform()]))
            }
          >
            {t("Add Platform")}
          </Button>
        </div>
        {platforms.map((platform, index) => (
          <Card key={`platform-${index}`} className="border mb-2">
            <CardBody>
              <Row>
                <Col md="3"><Input value={platform.id || ""} placeholder="id" onChange={e => { const next = cloneWebsiteConfig(platforms); next[index] = { ...next[index], id: e.target.value }; onChange(prev => setDeepValue(prev, "footer.social.platforms", next)) }} /></Col>
                <Col md={3}><Input value={platform.label || ""} placeholder={t("Label")} onChange={e => { const next = cloneWebsiteConfig(platforms); next[index] = { ...next[index], label: e.target.value }; onChange(prev => setDeepValue(prev, "footer.social.platforms", next)) }} /></Col>
                <Col md={3}><Input value={platform.url || ""} placeholder="URL" onChange={e => { const next = cloneWebsiteConfig(platforms); next[index] = { ...next[index], url: e.target.value }; onChange(prev => setDeepValue(prev, "footer.social.platforms", next)) }} /></Col>
                <Col md={1}><Button size="sm" color="danger" outline onClick={() => onChange(prev => setDeepValue(prev, "footer.social.platforms", platforms.filter((_, i) => i !== index)))}>×</Button></Col>
                <Col md={12}>
                  <ImageUrlField
                    label="Icon"
                    value={platform.iconUrl || ""}
                    onChange={url => {
                      const next = cloneWebsiteConfig(platforms)
                      next[index] = { ...next[index], iconUrl: url }
                      onChange(prev => setDeepValue(prev, "footer.social.platforms", next))
                    }}
                    t={t}
                    md={12}
                    purpose="social-icon"
                    compact
                  />
                </Col>
              </Row>
            </CardBody>
          </Card>
        ))}
      </Col>

      <Col md="12" className="mb-2">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <Label className="mb-0">{t("Payment Methods")}</Label>
          <Button size="sm" color="primary" outline onClick={() => onChange(prev => setDeepValue(prev, "footer.payments", [...payments, emptyPayment()]))}>
            {t("Add Payment Method")}
          </Button>
        </div>
        {payments.map((payment, index) => (
          <Card key={`payment-${index}`} className="border mb-2">
            <CardBody>
              <Row>
                <Col md={4}><Input value={payment.id || ""} placeholder="id" onChange={e => { const next = cloneWebsiteConfig(payments); next[index] = { ...next[index], id: e.target.value }; onChange(prev => setDeepValue(prev, "footer.payments", next)) }} /></Col>
                <Col md={4}><Input value={payment.label || ""} placeholder={t("Label")} onChange={e => { const next = cloneWebsiteConfig(payments); next[index] = { ...next[index], label: e.target.value }; onChange(prev => setDeepValue(prev, "footer.payments", next)) }} /></Col>
                <Col md={1}><Button size="sm" color="danger" outline onClick={() => onChange(prev => setDeepValue(prev, "footer.payments", payments.filter((_, i) => i !== index)))}>×</Button></Col>
                <Col md={12}>
                  <ImageUrlField
                    label="Payment Icon"
                    value={payment.iconUrl || ""}
                    onChange={url => {
                      const next = cloneWebsiteConfig(payments)
                      next[index] = { ...next[index], iconUrl: url }
                      onChange(prev => setDeepValue(prev, "footer.payments", next))
                    }}
                    t={t}
                    md={12}
                    purpose="payment-icon"
                    compact
                  />
                </Col>
              </Row>
            </CardBody>
          </Card>
        ))}
      </Col>

      <Col md="12"><h5 className="mt-2">{t("SEO")}</h5></Col>
      <LocalizedField form={form} path="seo.metaTitle" label="SEO Title" t={t} onChange={onChange} />
      <LocalizedField form={form} path="seo.metaDescription" label="SEO Description" t={t} onChange={onChange} />
      <ImageField form={form} path="seo.ogImage" label="OG Image" t={t} onChange={onChange} purpose="og-image" md={12} />
    </Row>
  )
}

export function FeaturesTab({ form, t, onChange, allowedFeatures = null }) {
  const visibleToggles = filterWebsiteBuilderFeatureToggles(FEATURE_TOGGLES, allowedFeatures)

  if (!visibleToggles.length) {
    return (
      <Row>
        <Col md="12">
          <p className="text-muted mb-0">
            {t(
              "No storefront feature toggles are included in your subscription plan. Contact your platform administrator to add order types or loyalty to your plan."
            )}
          </p>
        </Col>
      </Row>
    )
  }

  return (
    <Row>
      {visibleToggles.map(item => (
        <Col md="6" lg="4" key={item.key}>
          <Switch
            label={t(item.label)}
            isChecked={!!form.features?.[item.key]}
            onChange={checked => onChange(prev => setDeepValue(prev, `features.${item.key}`, checked))}
          />
        </Col>
      ))}
    </Row>
  )
}

export function PreviewPublishTab({
  form,
  t,
  onChange,
  tenantWebsiteUrl,
  publishedAt,
  publishedSnapshot,
  hasUnpublishedChanges,
  webThemes = [],
}) {
  const configuredUrl = normalizeWebsiteUrl(form?.preview?.websiteUrl)
  const openUrl = configuredUrl || normalizeWebsiteUrl(tenantWebsiteUrl)
  const selectedTheme = webThemes.find(item => item.slug === form?.webThemeSlug)
  const liveTheme = webThemes.find(item => item.slug === publishedSnapshot?.webThemeSlug)

  return (
    <Row>
      <Col md="12" lg="5" className="mb-4">
        <Card className="border h-100">
          <CardBody>
            <h5>{t("Publish status")}</h5>
            <p className="text-muted">
              {publishedAt
                ? t("Last published at {{date}}", {
                    date: new Date(publishedAt).toLocaleString(),
                  })
                : t("This website has not been published yet.")}
            </p>
            <p className="mb-2">
              {hasUnpublishedChanges
                ? t("You have unpublished draft changes.")
                : t("Live website matches the current draft.")}
            </p>
            {publishedSnapshot ? (
              <>
                <p className="text-muted mb-1">
                  {t("Layout")}: {t("Standard storefront")}
                </p>
                {liveTheme ? (
                  <p className="text-muted mb-0">
                    {t("Theme")}: {liveTheme.name}
                  </p>
                ) : null}
              </>
            ) : null}
            {selectedTheme && selectedTheme.slug !== publishedSnapshot?.webThemeSlug ? (
              <p className="text-warning small mb-0 mt-2">
                {t("Draft theme")}: {selectedTheme.name}
              </p>
            ) : null}
          </CardBody>
        </Card>
      </Col>

      <Col md="12" lg="7" className="mb-4">
        <Card className="border h-100">
          <CardBody>
            <h5>{t("Live website preview")}</h5>
            <p className="text-muted small">
              {t(
                "Save the website URL here to preview your tenant storefront in the iframe below. Draft changes appear on the live site only after you publish."
              )}
            </p>

            <div className="mb-3">
              <Label>{t("Customer website URL")}</Label>
              <Input
                type="url"
                value={form.preview?.websiteUrl || ""}
                placeholder={
                  tenantWebsiteUrl ||
                  t("https://your-store.example.com")
                }
                onChange={e =>
                  onChange(prev => setDeepValue(prev, "preview.websiteUrl", e.target.value))
                }
              />
              <small className="text-muted d-block mt-1">
                {tenantWebsiteUrl
                  ? t("Tenant domain on file: {{url}}", { url: tenantWebsiteUrl })
                  : t("No tenant domain is configured yet.")}
              </small>
            </div>

            <div className="d-flex flex-wrap gap-2 mb-3">
              {tenantWebsiteUrl ? (
                <Button
                  size="sm"
                  color="light"
                  onClick={() =>
                    onChange(prev =>
                      setDeepValue(prev, "preview.websiteUrl", tenantWebsiteUrl)
                    )
                  }
                >
                  {t("Use tenant domain")}
                </Button>
              ) : null}
              {openUrl ? (
                <Button
                  tag="a"
                  size="sm"
                  color="primary"
                  outline
                  href={openUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("Open website")}
                </Button>
              ) : null}
            </div>

            {configuredUrl ? (
              <>
                <div className="ratio ratio-16x9 border rounded overflow-hidden bg-light">
                  <iframe
                    title={t("Website preview")}
                    src={configuredUrl}
                    className="w-100 h-100 border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
                <p className="text-muted small mt-2 mb-0">
                  {t("Previewing saved URL: {{url}}", { url: configuredUrl })}
                </p>
              </>
            ) : (
              <div className="border rounded p-4 text-center text-muted bg-light">
                {tenantWebsiteUrl
                  ? t(
                      "Save a customer website URL above or click Use tenant domain to enable the live preview iframe."
                    )
                  : t("Add a customer website URL to enable the live preview iframe.")}
              </div>
            )}
          </CardBody>
        </Card>
      </Col>
    </Row>
  )
}

export function AnimationsTab({ form, t, onChange }) {
  return (
    <Row>
      <Col md="6">
        <Switch
          label={t("Enable animations")}
          isChecked={!!form.animations?.enabled}
          onChange={checked => onChange(prev => setDeepValue(prev, "animations.enabled", checked))}
        />
      </Col>
      <Col md="4">
        <div className="mb-3">
          <Label>{t("Page Transition")}</Label>
          <Input type="select" value={form.animations?.pageTransition || "fade"} onChange={e => onChange(prev => setDeepValue(prev, "animations.pageTransition", e.target.value))}>
            {WEBSITE_ANIMATION_OPTIONS.pageTransition.map(option => (
              <option key={option.value} value={option.value}>{t(option.label)}</option>
            ))}
          </Input>
        </div>
      </Col>
      <Col md="4">
        <div className="mb-3">
          <Label>{t("Section Reveal")}</Label>
          <Input type="select" value={form.animations?.sectionReveal || "fade-up"} onChange={e => onChange(prev => setDeepValue(prev, "animations.sectionReveal", e.target.value))}>
            {WEBSITE_ANIMATION_OPTIONS.sectionReveal.map(option => (
              <option key={option.value} value={option.value}>{t(option.label)}</option>
            ))}
          </Input>
        </div>
      </Col>
      <Col md="4">
        <div className="mb-3">
          <Label>{t("Hero Transition")}</Label>
          <Input type="select" value={form.animations?.heroTransition || "fade"} onChange={e => onChange(prev => setDeepValue(prev, "animations.heroTransition", e.target.value))}>
            {WEBSITE_ANIMATION_OPTIONS.heroTransition.map(option => (
              <option key={option.value} value={option.value}>{t(option.label)}</option>
            ))}
          </Input>
        </div>
      </Col>
      <TextField form={form} path="animations.durationMs" label="Duration (ms)" t={t} onChange={onChange} type="number" md={4} />
      <Col md="4">
        <div className="mb-3">
          <Label>{t("Easing")}</Label>
          <Input type="select" value={form.animations?.easing || "ease-out"} onChange={e => onChange(prev => setDeepValue(prev, "animations.easing", e.target.value))}>
            {WEBSITE_ANIMATION_OPTIONS.easing.map(option => (
              <option key={option.value} value={option.value}>{t(option.label)}</option>
            ))}
          </Input>
        </div>
      </Col>
    </Row>
  )
}
